import { useMemo} from 'react';
import Plot from 'react-plotly.js';
import { E3M3_TO_MCF, M3_TO_BBL } from '../../helpers/conversionFactors';
import { chartColors } from '../../helpers/chartColors';
import { fbaConversionFactors } from './fbaConversionFactors';

export const FBAEditProductionDataChart = ({ fbaData, fbaProcessedData, fbaPreviewData, currentTrace, units, darkMode, showRawData }) => {

  const data = useMemo(() => {
    return getChartData();

    function getChartData() {
      try {
        if (!fbaData?.length > 0) return [];

        let traces = [];

        if (currentTrace === 'fluids') {
          for (const trace of ['oil', 'condensate', 'gas', 'water']) {
            const fluidProcessedTrace = {
              x: fbaProcessedData?.map(d => d.days),
              y: fbaProcessedData?.map(d => getTraceData(d, trace)),
              mode: 'markers',
              type: 'scatter',
              xaxis: 'x1',
              name: `${trace?.charAt(0)?.toUpperCase()?.concat(trace?.substring(1, ))} (${getTraceUnits(trace)})`,
              marker: {
                color: 'transparent',
                line: {
                  width: 1,
                  color: getTraceColor(trace)
                },
                symbol: getTraceSymbol(trace),
                size: 4
              },
              legendgroup: trace,
              line: {
                width: 0,
                color: getTraceColor(trace)
              }
            };
            const fluidPreviewTrace = {
              x: fbaPreviewData?.map(d => d.days),
              y: fbaPreviewData?.map(d => getTraceData(d, trace)),
              mode: 'lines+markers',
              type: 'scatter',
              xaxis: 'x1',
              name: `${trace?.charAt(0)?.toUpperCase()?.concat(trace?.substring(1, ))} Preview (${getTraceUnits(trace)})`,
              line: {
                width: 3,
                color: getTraceColor(trace)
              },
              marker: {
                color: 'transparent',
                line: {
                  width: 1,
                  color: getTraceColor(trace)
                },
                symbol: getTraceSymbol(trace),
                size: 4
              },
              legendgroup: trace,
              showlegend: false
            };

            traces = traces.concat(fluidProcessedTrace, fluidPreviewTrace);
          };
        } else if (currentTrace === 'pressure') {
          for (const trace of ['pwh', 'pwf']) {
            const pressureProcessedTrace = {
              x: fbaProcessedData?.map(d => d.days),
              y: fbaProcessedData?.map(d => getTraceData(d, trace)),
              mode: 'markers',
              type: 'scatter',
              xaxis: 'x1',
              name: `${trace?.charAt(0)?.toUpperCase()?.concat(trace?.substring(1, ))} (${getTraceUnits(trace)})`,
              marker: {
                color: 'transparent',
                line: {
                  width: 1,
                  color: getTraceColor(trace)
                },
                symbol: getTraceSymbol(trace),
                size: 4
              },
              line: {
                width: 0,
                color: getTraceColor(trace)
              },
              legendgroup: trace,
            };
            const pressurePreviewTrace = {
              x: fbaPreviewData?.map(d => d.days),
              y: fbaPreviewData?.map(d => getTraceData(d, trace)),
              mode: 'line',
              type: 'scatter',
              xaxis: 'x1',
              name: `${trace?.charAt(0)?.toUpperCase()?.concat(trace?.substring(1, ))} Preview (${getTraceUnits(trace)})`,
              line: {
                width: 3,
                color: getTraceColor(trace)
              },
              legendgroup: trace,
              showlegend: false
            };

            traces = traces.concat(pressureProcessedTrace, pressurePreviewTrace);
          };
        };
  

        return traces;
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  
    function getTraceData(data, trace) {
      switch(trace) {
        case 'oil':
          return units === 'metric' ? (data.oil / M3_TO_BBL) : data.oil;
        case 'condensate':
          return units === 'metric' ? (data.condensate / M3_TO_BBL) : data.condensate;
        case 'gas':
          return units === 'metric' ? (data.gas / E3M3_TO_MCF) : data.gas;
        case 'water':
          return units === 'metric' ? (data.water / M3_TO_BBL) : data.water;
        case 'pwh':
          return units === 'metric' ? (data.pwh * fbaConversionFactors?.pwh) : data.pwh;
        case 'pwf':
          return units === 'metric' ? (data.pwf * fbaConversionFactors?.pwh) : data.pwf;
        default:
          return 0;
      };
    };
  
    function getTraceSymbol(trace) {
      switch(trace) {
        case 'oil':
          return 'circle';
        case 'condensate':
          return 'triangle';
        case 'gas':
          return 'diamond';
        case 'water':
          return 'square';
        case 'pwh':
          return 'circle';
        case 'pwf':
          return 'triangle';
        default:
          return 'circle';
      };
    };
  
    function getTraceColor(trace) {
      switch(trace) {
        case 'oil':
          return chartColors[1];
        case 'condensate':
          return chartColors[2];
        case 'gas':
          return chartColors[3];
        case 'water':
          return chartColors[4];
        case 'pwh':
          return chartColors[5];
        case 'pwf':
          return chartColors[6];
        default:
          return chartColors[1];
      };
    };

    function getTraceUnits(trace) {
      switch(trace) {
        case 'oil':
          return units === 'metric' ? 'm3/day' : 'bbl/day';
        case 'condensate':
          return units === 'metric' ? 'm3/day' : 'bbl/day';
        case 'gas':
          return units === 'metric' ? 'E3m3/day' : 'mscf/day';
        case 'water':
          return units === 'metric' ? 'm3/day' : 'bbl/day';
        case 'pwh':
          return units === 'metric' ? 'kPa' : 'psi';
        case 'pwf':
          return units === 'metric' ? 'kPa' : 'psi';
        default:
          return '';
      };
    };
  }, [fbaData, currentTrace, fbaProcessedData, fbaPreviewData, units]);


  const layout = {
    // grid: {rows: 3, columns: 2, pattern: 'independent'},
    yaxis: { // y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `${currentTrace === 'fluids' ? 'Fluid Rates' : 'Pressure'} ${getAxisUnits()}`,
        font: {
          size: 12
        }
      },
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0, 1],
    },
    xaxis: { // Days x axis
      // showgrid: false,
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Time (days)',
        standoff: 10,
        font: {
          size: 12
        }
      },
      domain: [0, 1],
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
      // showspikes: true,
      // spikemode: 'toaxis' // or 'across' or 'marker'
    },
    hovermode: "x",
    hoverdistance: 100, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      orientation: "h",
      yanchor: "top",
      yref: 'paper',
      y: -0.125,
    },
    uirevision:'true',
    annotations: [
      {
        text: currentTrace === 'fluids' ? 'Fluid Rates' : 'Pressure',
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: 1.1, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
    ],
    margin: {"t": 50, "l": 60, "r": 40, "b": 50},
    images: [
      {
        x: 0.05,
        y: 1.015,
        sizex: 0.1,
        sizey: 0.075,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_${darkMode ? 'white' : 'primary'}.png`,
        xref: "paper",
        yref: "paper",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 1
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: (darkMode ? 'transparent' : '#fff')
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "FBA inputs-" + (new Date().toISOString().split('T')[0]),
      height: 480,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%', minWidth: '600px'}}
    />
  );


  function getAxisUnits() {
    if (currentTrace === 'fluids') {
      return units === 'metric' ? '(m3/day and E3m3/day)' : '(bbl/day and mscf/day)';
    } else {
      return units === 'metric' ? 'kPa' : 'psi';
    };
  };
};
